import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { Pager } from "@/components/design/Pager";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { NoBackend } from "@/components/common/NoBackend";
import { FileDropZone } from "@/components/common/FileDropZone";
import { usePendingScope } from "@/lib/pending-edits";
import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { userErrorMessage } from "@/lib/user-error";
import { DocItemRow } from "./DocItemRow";
import { DocPreviewCard } from "./DocPreviewCard";
import { DocumentFilterBuilder } from "./DocumentFilterBuilder";
import { FolderRow, NewFolderRow } from "./FolderRow";
import type { DocumentFilterDefinition, DocumentFilterKey, DocumentItem } from "./types";

const NEW_FILE_WINDOW_MS = 48 * 60 * 60 * 1000;
const DOCS_PER_PAGE = 24;
const GROUPS_PER_PAGE = 18;
const NO_GROUPS: readonly { id: string; name: string }[] = [];

type UploadProps = Omit<ComponentProps<typeof FileDropZone>, "onFiles"> & { onFiles?: (files: FileList) => void };
function UploadSurface({ onFiles, ...props }: UploadProps) {
  return onFiles ? <FileDropZone {...props} onFiles={onFiles} /> : <div className={props.className}>{props.children}</div>;
}

export interface DocumentLibraryFolders {
  items: readonly { id: string; name: string }[];
  onCreate: (name: string) => Promise<boolean>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string, mode: "keep" | "withFiles") => Promise<void>;
  /** Return ids that failed; only successful moves leave the selection. */
  onMove: (ids: readonly string[], groupId: string | null) => Promise<readonly string[]>;
  error?: ReactNode;
}

export interface DocumentLibraryProps {
  scopeId: string;
  items: readonly DocumentItem[];
  title?: ReactNode;
  titleVariant?: "default" | "hero";
  /** The enclosing section supplies the surface and heading (for example, a labelled tab). */
  embedded?: boolean;
  description: ReactNode;
  loading?: boolean;
  busy?: boolean;
  error?: ReactNode;
  accept?: string;
  folders?: DocumentLibraryFolders;
  onUpload?: (files: FileList | File[], groupId: string | null) => Promise<void>;
  onRename?: (id: string, name: string) => Promise<void>;
  validateRenames?: (changes: ReadonlyArray<readonly [string, string]>) => void;
  /** Return ids that failed; successful rows leave the selection. */
  onDelete?: (ids: readonly string[]) => Promise<readonly string[]>;
  onOpen: (id: string) => void | Promise<void>;
  onDownload?: (id: string) => void | Promise<void>;
  loadPreview?: (id: string) => Promise<string | null>;
  onFilterChange?: (filters: { query: string; fileType: string }) => void;
  assistantAnchorPrefix?: string;
  idPrefix?: string;
}

/** Shared document browser. Callers own data, authorization, persistence and optional capabilities. */
export function DocumentLibrary({ scopeId, items: allItems, title, titleVariant = "default", embedded = false, description,
  loading = false, busy: externalBusy = false, error, accept, folders, onUpload, onRename, validateRenames,
  onDelete, onOpen, onDownload, loadPreview, onFilterChange, assistantAnchorPrefix, idPrefix }: DocumentLibraryProps) {
  const intl = useIntl();
  const docGroups = folders?.items ?? NO_GROUPS;
  const [nowMs] = useState(() => Date.now());
  const [docQuery, setDocQuery] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const busy = externalBusy || actionBusy;
  const [uploading, setUploading] = useState(false);
  const uploadBusy = externalBusy || uploading;
  const anchor = (part: string) => assistantAnchorPrefix ? `${assistantAnchorPrefix}.${part}` : undefined;
  const eid = (part: string) => idPrefix ? `${idPrefix}-${part}` : undefined;
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [fileTypeFilter, setFileTypeFilter] = useState<string>("all");
  const [selectedDocFilters, setSelectedDocFilters] = useState<DocumentFilterKey[]>([]);
  // Client-side pager for the loose/Unsorted list. The full set is already
  // loaded by the caller; this only governs how many rows render at
  // once. Folders are bounded buckets and show their full contents unpaged.
  const [loosePage, setLoosePage] = useState(0);
  const [groupsPage, setGroupsPage] = useState(0);
  // Accordion: folders are collapsible full-width sections (several can be
  // open at once); loose files live in the trailing "Unsortiert" section.
  const [expandedFolders, setExpandedFolders] = useState<ReadonlySet<string>>(new Set());
  const [docView, setDocView] = useState<"list" | "compact">("compact");
  const [dropTarget, setDropTarget] = useState<string | null>(null); // group id or "loose"
  const [draggingId, setDraggingId] = useState<string | null>(null); // document being dragged
  // Which row is currently in inline rename mode. Only one row at a time.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  // Pending inline renames (id → new name). The global SaveBar (smycles-toast)
  // surfaces them and persists on "Speichern".
  const [pendingRenames, setPendingRenames] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { onFilterChange?.({ query: docQuery, fileType: fileTypeFilter }); }, [docQuery, fileTypeFilter, onFilterChange]);

  async function runAction(work: () => void | Promise<unknown>) {
    if (busy) return;
    setActionBusy(true); setActionError(null);
    try { await work(); }
    catch (failure) { setActionError(localizedMessageOf(failure) ?? userErrorMessage(intl, failure)); }
    finally { setActionBusy(false); }
  }
  const handleUpload = (files: FileList | File[], groupId: string | null = null) => runAction(async () => {
    setUploading(true);
    try { await onUpload?.(files, groupId); } finally { setUploading(false); }
  });
  const handleCreateGroup = (name: string) => folders?.onCreate(name) ?? Promise.resolve(false);
  const handleRenameGroup = (id: string, name: string) => runAction(() => folders?.onRename(id, name));
  const handleDeleteGroup = (id: string, mode: "keep" | "withFiles") => runAction(() => folders?.onDelete(id, mode));
  const moveDocToGroup = (id: string, groupId: string | null) => runAction(() => folders?.onMove([id], groupId));
  const toggleSelect = (id: string) => setSelectedIds(previous => {
    const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  async function bulkDelete() {
    await runAction(async () => {
      if (!onDelete) return;
      const failed = await onDelete(Array.from(selectedIds));
      setSelectedIds(new Set(failed));
      setPendingRenames(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => !selectedIds.has(id) || failed.includes(id))));
    });
  }
  async function bulkMove(groupId: string) {
    await runAction(async () => {
      if (!folders) return;
      const failed = await folders.onMove(Array.from(selectedIds), groupId);
      setSelectedIds(new Set(failed));
    });
  }
  const recordRename = (id: string, name: string, original: string) => setPendingRenames(previous => {
    const next = { ...previous }; if (name === original) delete next[id]; else next[id] = name; return next;
  });
  // @adr SPARK-0005-unified-deferred-save-bar@1: both consumers stage renames in the shared SaveBar.
  usePendingScope(scopeId, {
    dirty: Boolean(onRename) && Object.keys(pendingRenames).length > 0,
    save: async () => {
      if (!onRename) return;
      const changes = Object.entries(pendingRenames);
      if (changes.some(([, name]) => !name.trim())) throw new LocalizedError(intl.formatMessage({ id: "service.error.nameRequired" }));
      if (changes.some(([, name]) => Array.from(name.trim()).length > 255)) throw new LocalizedError(intl.formatMessage({ id: "documents.nameTooLong" }));
      validateRenames?.(changes);
      setActionBusy(true); setActionError(null);
      try {
        const results = await Promise.allSettled(changes.map(async ([id, name]) => {
          await onRename(id, name.trim());
          setPendingRenames(previous => { const next = { ...previous }; delete next[id]; return next; });
        }));
        const failure = results.find(result => result.status === "rejected");
        if (failure?.status === "rejected") throw failure.reason;
      } catch (failure) {
        const message = localizedMessageOf(failure) ?? userErrorMessage(intl, failure);
        setActionError(message); throw new LocalizedError(message);
      } finally { setActionBusy(false); }
    },
    discard: () => { setPendingRenames({}); setRenamingId(null); setActionError(null); },
  });
  const sourceOptions = useMemo(
    () => Array.from(new Set(allItems.map((it) => it.source))).sort((a, b) => a.localeCompare(b)),
    [allItems],
  );
  const fileTypeOptions = useMemo(
    () => Array.from(new Set(allItems.map((it) => it.fileType))).sort((a, b) => a.localeCompare(b)),
    [allItems],
  );
  const items = allItems.filter((it) => {
    const queryMatch = !docQuery.trim() || it.name.toLowerCase().includes(docQuery.trim().toLowerCase());
    const sourceMatch = sourceFilter === "all" || it.source === sourceFilter;
    const fileTypeMatch = fileTypeFilter === "all" || it.fileType === fileTypeFilter;
    return queryMatch && sourceMatch && fileTypeMatch;
  });
  const hasDocumentFilters = Boolean(docQuery.trim()) || sourceFilter !== "all" || fileTypeFilter !== "all";
  const documentFilters: readonly DocumentFilterDefinition[] = [
    {
      key: "source",
      label: intl.formatMessage({ id: "service.page.filterSource", defaultMessage: "Source" }),
      options: sourceOptions,
      value: sourceFilter,
      onChange: (next) => {
        setSourceFilter(next);
        setLoosePage(0);
      },
    },
    {
      key: "fileType",
      label: intl.formatMessage({ id: "service.page.filterFileType", defaultMessage: "File type" }),
      options: fileTypeOptions,
      value: fileTypeFilter,
      onChange: (next) => {
        setFileTypeFilter(next);
        setLoosePage(0);
      },
    },
  ];
  const resetDocumentFilters = () => {
    setDocQuery("");
    setSourceFilter("all");
    setFileTypeFilter("all");
    setSelectedDocFilters([]);
    setLoosePage(0);
  };
  const selectedItems = allItems.filter((it) => selectedIds.has(it.id));
  const selectionHasCv = selectedItems.some((it) => !it.canMove);

  // Accordion partition (filtered set): each folder section shows its items
  // when expanded; loose items (no folder, incl. CV drafts) close the list.
  // Folder badge counts use the unfiltered set so a filtered search still
  // shows the real folder size. No nested folders - a folder holds only items.
  const looseItems = items.filter((it) => it.groupId === null);
  const looseTotalPages = Math.max(1, Math.ceil(looseItems.length / DOCS_PER_PAGE));
  // Clamp on read so deleting/moving items off the last page (or a filter that
  // shrinks the list) can't strand the user on a now-empty page.
  const loosePageClamped = Math.min(loosePage, looseTotalPages - 1);
  const pagedLooseItems = looseItems.slice(
    loosePageClamped * DOCS_PER_PAGE,
    loosePageClamped * DOCS_PER_PAGE + DOCS_PER_PAGE,
  );
  // Folders paginate independently of the loose list, GROUPS_PER_PAGE at a time.
  // Same clamp-on-read guard so deleting a folder off the last page can't strand
  // the user on a now-empty page.
  const groupsTotalPages = Math.max(1, Math.ceil(docGroups.length / GROUPS_PER_PAGE));
  const groupsPageClamped = Math.min(groupsPage, groupsTotalPages - 1);
  const pagedGroups = docGroups.slice(
    groupsPageClamped * GROUPS_PER_PAGE,
    groupsPageClamped * GROUPS_PER_PAGE + GROUPS_PER_PAGE,
  );
  const folderItemCount = (gid: string) => allItems.filter((it) => it.groupId === gid).length;
  const toggleFolder = (gid: string) =>
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  // "list" view = discovery-style preview cards (server-rendered thumbnails);
  // "compact" view = dense one-line rows.
  const itemGridClass =
    docView === "list"
      ? // Cards follow the available main-container width, including an open assistant drawer.
        "grid grid-cols-2 gap-3 @sm:grid-cols-4 @xl:grid-cols-8"
      : "grid grid-cols-1 gap-2 @md:grid-cols-2 @lg:grid-cols-3";

  const renderItemRow = (it: DocumentItem) => {
    const isNew = !!it.when && nowMs - Date.parse(it.when) < NEW_FILE_WINDOW_MS;
    const shared = {
      kind: it.kind,
      name: pendingRenames[it.id] ?? it.name,
      when: it.when,
      source: it.source,
      sourceDisplay: it.sourceDisplay,
      isNew,
      isRenaming: renamingId === it.id,
      selected: selectedIds.has(it.id),
      onToggleSelect: onDelete || folders?.onMove ? () => toggleSelect(it.id) : undefined,
      onStartRename: onRename ? () => setRenamingId(it.id) : undefined,
      onRename: (newName: string) => recordRename(it.id, newName, it.name),
      onStopRename: () => setRenamingId(null),
      // Opening and downloading are owned by the consumer, including editor-backed items.
      onOpen: () => void runAction(() => onOpen(it.id)),
      onDownload: onDownload && it.canDownload !== false ? () => void runAction(() => onDownload(it.id)) : undefined,
      // Only real documents are draggable into folders. Inline rename now lives
      // inside the row's name <button> — drag is enabled regardless.
      draggable: Boolean(folders?.onMove && it.canMove && renamingId !== it.id && !busy),
      disabled: busy,
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", it.id);
        setDraggingId(it.id);
      },
      onDragEnd: () => {
        setDraggingId(null);
        setDropTarget(null);
      },
    };
    return docView === "list" ? (
      <DocPreviewCard
        key={it.id}
        idPrefix={eid(`card-${it.id}`)}
        previewSrc={it.preview}
        documentId={it.id} loadPreview={loadPreview}
        cvGenerator={it.cvGenerator}
        {...shared}
      />
    ) : (
      <DocItemRow key={it.id} idPrefix={eid(`row-${it.id}`)} {...shared} />
    );
  };

  const dropHandlers = (target: string, groupId: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!draggingId) return;
      e.preventDefault();
      e.stopPropagation();
      if (dropTarget !== target) setDropTarget(target);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Native dragleave bubbles up from child elements, so moving between a
      // folder's inner rows/icons would clear the highlight and flicker. Only
      // clear when the pointer actually leaves this drop zone (relatedTarget
      // outside currentTarget; null = left the window entirely).
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDropTarget((t) => (t === target ? null : t));
    },
    onDrop: (e: React.DragEvent) => {
      // Only handle internal document drags here. File drops (no draggingId)
      // must bubble to the box's upload handler, which uploads and clears the
      // drag affordance — otherwise the blur overlay stays stuck on a folder/
      // loose-zone drop.
      if (!draggingId) return;
      e.preventDefault();
      e.stopPropagation();
      void moveDocToGroup(draggingId, groupId);
      setDropTarget(null);
      setDraggingId(null);
    },
  });

  const uploadButton = onUpload && <Button id={eid("upload")} disabled={busy || loading} onClick={() => fileInputRef.current?.click()}
    data-assistant-anchor={anchor("upload")}>
    <FormattedMessage id={uploadBusy ? "service.page.uploading" : "service.page.uploadFile"} />
  </Button>;
  const uploadHints = <>
    <span className="text-smycles-micro text-ink"><FormattedMessage id="service.page.orDragAndDrop" /></span>
    <span className="text-smycles-micro text-ink-muted"><FormattedMessage id="service.page.maxPerFile" /></span>
  </>;
  const flatList = embedded && !folders;
  // Empty list doubles as the drop field, so the first upload needs no header button (@adr SPARK-0022-file-drop-fields@2).
  // A rejected upload keeps the field: the error is shown below it, and the retry must not need the button.
  const emptyDropField = Boolean(onUpload) && !loading && !hasDocumentFilters && allItems.length === 0;

  return (
    <div className={embedded ? undefined : "box bg-surface p-6"}>
      {onUpload && <input
        ref={fileInputRef}
        id="documents-file-input"
        accept={accept}
        disabled={busy || loading}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void handleUpload(e.target.files);
          e.target.value = "";
        }}
      />}
      {!embedded && <div className="flex flex-col items-start gap-4 @md:flex-row @md:justify-between">
        <div className="min-w-0 w-full @md:w-auto @md:flex-1">
          <BoxHeader
            title={title ?? intl.formatMessage({ id: "service.page.documentsTitle", defaultMessage: "Documents" })}
            variant={titleVariant}
          />
          <div className="smycles-field-meta leading-snug text-ink">
            {uploadBusy ? (
              <FormattedMessage id="service.page.uploading" defaultMessage="Uploading …" />
            ) : loading ? (
              <FormattedMessage id="common.loading" defaultMessage="Loading…" />
            ) : description}
          </div>
        </div>

        {onUpload && <div className="flex shrink-0 flex-col items-center gap-1.5">
          {uploadButton}
          {uploadHints}
        </div>}

      </div>}

      <div className={`${embedded ? "" : "mt-5 "}flex flex-wrap items-center gap-2`} data-assistant-anchor={anchor("filters")}>
        <div className="box-flat flex w-full max-w-xs items-center bg-surface px-3 py-1.5">
            <input
              id={eid("filter")}
              value={docQuery}
              disabled={loading}
              aria-label={intl.formatMessage({ id: "service.page.filterPlaceholder" })}
              onChange={(e) => {
                setDocQuery(e.target.value);
                setLoosePage(0);
              }}
              placeholder={intl.formatMessage({
                id: "service.page.filterPlaceholder",
                defaultMessage: "Filter your available documents …",
              })}
              className="min-w-0 flex-1 bg-transparent text-smycles-meta placeholder:text-ink-muted focus:outline-none"
            />
          </div>

        <DocumentFilterBuilder idPrefix={eid("filterbuilder")} disabled={loading}
          filters={documentFilters}
          selected={selectedDocFilters}
          setSelected={setSelectedDocFilters}
        />

        <div className={`ml-auto flex flex-wrap items-center gap-2 ${embedded ? "justify-end" : ""}`}>
          <span className="smycles-field-label text-ink">
            {loading ? <FormattedMessage id="common.loading" defaultMessage="Loading…" /> : !error && <FormattedMessage
              id="service.page.documentsCount"
              defaultMessage="{total, plural, one {# document} other {# documents}}"
              values={{ total: allItems.length }}
            />}
          </span>
          {hasDocumentFilters ? (
            <button
              id={eid("filter-reset")}
              type="button"
              onClick={resetDocumentFilters}
              className="smycles-field-label ml-1 text-ink hover:text-ink-accent"
            >
              <FormattedMessage id="service.page.reset" defaultMessage="Reset" />
            </button>
          ) : null}
          <SegmentedControl
            id={eid("view")}
            value={docView}
            onChange={setDocView}
            options={[
              {
                value: "list",
                label: intl.formatMessage({ id: "service.page.viewPreview", defaultMessage: "Preview" }),
              },
              {
                value: "compact",
                label: intl.formatMessage({ id: "service.page.viewCompact", defaultMessage: "Compact" }),
              },
            ]}
          />
          {embedded && uploadButton}
        </div>
      </div>
      {embedded && onUpload && <div className="mt-2 flex flex-wrap justify-end gap-x-3 gap-y-1">{uploadHints}</div>}

      {/* Bulk-actions bar appears the moment any item is selected. The
          checkboxes themselves are always available via row hover, so this
          bar acts as the "you have an active multi-selection" affordance. */}
      {selectedItems.length > 0 && (onDelete || folders?.onMove) ? (
        <div className="box-flat mt-3 flex flex-wrap items-center gap-2 bg-ink/[0.06] px-3 py-2">
          <span id={eid("selection")} className="smycles-field-label text-ink">
            <FormattedMessage
              id="service.page.selectedCount"
              defaultMessage="{count} selected"
              values={{ count: selectedItems.length }}
            />
          </span>
          {confirmBulkDelete ? (
            <>
              <span className="text-smycles-label text-ink">
                <FormattedMessage
                  id="service.page.confirmDelete"
                  defaultMessage="Really delete?"
                />
              </span>
              <Button id={eid("delete-confirm")} variant="destructive" size="sm" disabled={busy}
                onClick={() => {
                  setConfirmBulkDelete(false);
                  void bulkDelete();
                }}
              >
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </Button>
              <Button variant="secondary" size="sm" disabled={busy}
                onClick={() => setConfirmBulkDelete(false)}
              >
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
            </>
          ) : (
            <>
              {onDelete && <Button
                id={eid("delete")}
                variant="secondary"
                size="sm"
                disabled={busy || selectedItems.length === 0}
                onClick={() => setConfirmBulkDelete(true)}
              >
                <FormattedMessage id="common.delete" defaultMessage="Delete" />
              </Button>}
              {folders && <div className="relative">
                <Button
                  id={eid("move")}
                  variant="secondary"
                  disabled={busy || selectedItems.length === 0 || selectionHasCv || docGroups.length === 0}
                  title={
                    folders && selectionHasCv
                      ? intl.formatMessage({
                          id: "service.page.cvNotMovable",
                          defaultMessage: "CV drafts cannot be moved into folders",
                        })
                      : docGroups.length === 0
                        ? intl.formatMessage({
                            id: "service.page.noFolders",
                            defaultMessage: "No folders available",
                          })
                        : undefined
                  }
                  onClick={() => setMoveOpen((o) => !o)}
                  size="sm"
                  className="gap-1"
                >
                  <FormattedMessage
                    id="service.page.moveToFolder"
                    defaultMessage="Move to folder"
                  />
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </Button>
                {moveOpen ? (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setMoveOpen(false)} />
                    <div className="absolute left-0 top-full z-30 mt-1 w-56 overflow-auto rounded-md bg-surface py-1 shadow-lg ring-1 ring-ink/10">
                      {docGroups.map((g) => (
                        <MenuItem
                          key={g.id}
                          id={eid(`move-option-${g.id}`)}
                          onClick={() => {
                            setMoveOpen(false);
                            void bulkMove(g.id);
                          }}
                        >
                          {g.name}
                        </MenuItem>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>}
              {folders && selectionHasCv ? (
                <span id={eid("move-blocked")} className="text-smycles-micro text-ink">
                  <FormattedMessage
                    id="service.page.onlyRealFilesMovable"
                    defaultMessage="Only real files can be moved into folders, not CV generator entries."
                  />
                </span>
              ) : null}
              {selectedItems.length > 0 ? (
                <button
                  id={eid("selection-clear")}
                  type="button"
                  onClick={() =>
                    folders && selectionHasCv
                      ? // Deselect only the blocking CV-Generator entries, so the
                        // remaining real files stay selected and become movable.
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          for (const it of allItems) if (!it.canMove) next.delete(it.id);
                          return next;
                        })
                      : setSelectedIds(new Set())
                  }
                  className="smycles-field-label text-ink hover:text-ink-accent"
                >
                  {folders && selectionHasCv ? (
                    <FormattedMessage
                      id="service.page.removeBlockingSelection"
                      defaultMessage="Remove blocking selection"
                    />
                  ) : (
                    <FormattedMessage
                      id="service.page.clearSelection"
                      defaultMessage="Clear selection"
                    />
                  )}
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}


        <div className="mt-4 flex flex-col gap-3" data-assistant-anchor={anchor("list")}>
          {folders && <div className="grid grid-cols-1 items-start gap-2 @sm:grid-cols-2 @xl:grid-cols-3">
            {pagedGroups.map((g) => {
              const expanded = expandedFolders.has(g.id);
              const inFolder = items.filter((it) => it.groupId === g.id);
              return (
                <UploadSurface
                  key={g.id}
                  variant="overlay"
                  className="rounded-md"
                  onFiles={onUpload ? files => void handleUpload(files, g.id) : undefined}
                  accept={accept} disabled={busy || loading}
                  dropLabel={intl.formatMessage({
                    id: "service.page.dropIntoFolder",
                    defaultMessage: "Drop to upload into this folder",
                  })}
                >
                <div
                  className={
                    expanded
                      ? `overflow-hidden rounded-md bg-page/40 transition ${
                          dropTarget === g.id ? "smycles-drop-target ring-2 ring-accent-decoration" : "ring-1 ring-ink/15"
                        }`
                      : ""
                  }
                  // Expanded: the WHOLE folder box (header + contents) is the
                  // internal-move drop zone (file uploads use the FileDropZone wrapper).
                  {...(expanded ? dropHandlers(g.id, g.id) : {})}
                >
                  <FolderRow
                    idPrefix={eid(`folder-${g.id}`)}
                    name={g.name}
                    count={folderItemCount(g.id)}
                    isOpen={expanded}
                    isDropTarget={dropTarget === g.id}
                    onOpen={() => toggleFolder(g.id)}
                    onRename={(name) => handleRenameGroup(g.id, name)}
                    onDelete={(mode) => handleDeleteGroup(g.id, mode)}
                    contentsLoading={loading}
                    {...dropHandlers(g.id, g.id)}
                  />
                  {expanded ? (
                    inFolder.length === 0 ? (
                      <div id={eid(`folder-${g.id}-empty`)} className="px-3 py-3 text-smycles-label text-ink-muted">
                        {hasDocumentFilters ? (
                          <FormattedMessage
                            id="service.page.noMatchesInFolder"
                            defaultMessage="No matches in this folder"
                          />
                        ) : (
                          <FormattedMessage
                            id="service.page.folderEmpty"
                            defaultMessage="Folder is empty - drag documents onto the folder tile."
                          />
                        )}
                      </div>
                    ) : (
                      // Single column inside the folder's own column width.
                      <div id={eid(`folder-${g.id}-items`)} className="grid grid-cols-1 gap-2 p-2">
                        {inFolder.map(renderItemRow)}
                      </div>
                    )
                  ) : null}
                </div>
                </UploadSurface>
              );
            })}
            <NewFolderRow idPrefix={eid("newfolder")} onCreate={handleCreateGroup} error={typeof folders.error === "string" ? folders.error : null} />
            {folders?.error && typeof folders.error !== "string" && <div className="col-span-full">{folders.error}</div>}
          </div>}

          <Pager idPrefix={eid("folderpager")} page={groupsPageClamped} totalPages={groupsTotalPages} onChange={setGroupsPage} />

          <UploadSurface
            id="documents-dropzone"
            variant={emptyDropField ? "zone" : "overlay"}
            clickToOpen={emptyDropField}
            className="rounded-md"
            onFiles={onUpload ? files => void handleUpload(files, null) : undefined}
            accept={accept} disabled={busy || loading}
            label={intl.formatMessage({ id: "service.page.noDocuments", defaultMessage: "No documents" })}
            hint={intl.formatMessage({
              id: "service.page.emptyDropHint",
              defaultMessage: "Drag files here or click to upload",
            })}
          >
          {emptyDropField ? null : <div
            id={eid("unsorted")}
            className={flatList ? "transition" : `rounded-md border border-dashed p-1 transition ${
              dropTarget === "loose" ? "smycles-drop-target border-2 border-accent-decoration bg-ink/[0.06]" : "border-ink/10"
            }`}
            {...dropHandlers("loose", null)}
          >
            {!flatList && <div className="smycles-field-label px-2 py-1 text-ink">
              {folders ? <FormattedMessage id="service.page.unsorted" defaultMessage="Unsorted" /> : title ?? <FormattedMessage id="service.page.documentsTitle" />}
            </div>}
            {looseItems.length === 0 ? (
              <div className={flatList ? "py-10 text-center smycles-field-meta text-ink-muted" : "px-2 py-3 text-smycles-label text-ink-muted"}>
                {hasDocumentFilters ? (
                  <FormattedMessage id="service.page.noMatches" defaultMessage="No matches" />
                ) : (
                  loading ? <FormattedMessage id="common.loading" defaultMessage="Loading…" /> : error ? null : allItems.length === 0
                    ? flatList ? <FormattedMessage id="service.page.noDocuments" />
                      : <NoBackend label={intl.formatMessage({ id: "service.page.noDocuments", defaultMessage: "No documents" })} />
                    : <FormattedMessage id="service.page.allInFolders" defaultMessage="Everything filed in folders" />
                )}
              </div>
            ) : (
              <>
                <div className={`${flatList ? "" : "p-1 "}${itemGridClass}`}>{pagedLooseItems.map(renderItemRow)}</div>
                <Pager idPrefix={eid("pager")} page={loosePageClamped} totalPages={looseTotalPages} onChange={setLoosePage} />
              </>
            )}
          </div>}
          </UploadSurface>
        </div>

      {error}
      {actionError && !error && <p role="alert" className="mt-3 smycles-field-meta text-ink">{actionError}</p>}



    </div>

  );
}
